import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { generateJsonExtractionSql } from '../utils/sqlGenerator';

export const MAX_PATH_LENGTH = 200;

export function isValidJsonPath(path: string): boolean {
  if (!path) return true; // Empty is valid (just not ready for SQL generation)
  return /^[a-zA-Z0-9.\[\]_\-*#]+$/.test(path);
}

function isValidJsonKey(key: string): boolean {
  if (!key) return true;
  return !/['\\]/.test(key);
}

interface JsonExtractionBuilderProps {
  columnName: string;
  onSqlGenerate: (sql: string) => void;
}

const RETURN_TYPES = [
  { value: 'VARCHAR', label: 'VARCHAR (Text)' },
  { value: 'INTEGER', label: 'INTEGER' },
  { value: 'BIGINT', label: 'BIGINT (Large Integer)' },
  { value: 'DOUBLE', label: 'DOUBLE (Decimal)' },
  { value: 'DECIMAL(10,2)', label: 'DECIMAL (Currency)' },
  { value: 'BOOLEAN', label: 'BOOLEAN' },
  { value: 'DATE', label: 'DATE' },
  { value: 'TIMESTAMP', label: 'TIMESTAMP' },
];

export const JsonExtractionBuilder: React.FC<JsonExtractionBuilderProps> = ({
  columnName,
  onSqlGenerate,
}) => {
  const [extractionMode, setExtractionMode] = useState<'single_field' | 'nested_path'>('single_field');
  const [fieldName, setFieldName] = useState('');
  const [jsonPath, setJsonPath] = useState('');
  const [returnType, setReturnType] = useState('VARCHAR');
  const [safeCast, setSafeCast] = useState(true);
  const [pathError, setPathError] = useState<string | null>(null);
  const [keyError, setKeyError] = useState<string | null>(null);

  useEffect(() => {
    const currentValue = extractionMode === 'single_field' ? fieldName : jsonPath;
    if (!currentValue.trim()) return;

    // Don't generate SQL when there's a validation error
    const hasError = extractionMode === 'single_field' ? keyError : pathError;
    if (hasError) return;

    const sql = generateJsonExtractionSql(columnName, {
      extractionMode,
      fieldName,
      jsonPath,
      returnType,
      safeCast,
    });

    if (sql) {
      onSqlGenerate(sql);
    }
  }, [extractionMode, fieldName, jsonPath, returnType, safeCast, columnName, onSqlGenerate, keyError, pathError]);

  return (
    <div className="space-y-4" role="group" aria-labelledby="json-extraction-heading">
      <h3 id="json-extraction-heading" className="sr-only">JSON Extraction Configuration</h3>

      <div>
        <Label htmlFor="json-extraction-mode">Extraction Mode</Label>
        <Select value={extractionMode} onValueChange={(v) => setExtractionMode(v as 'single_field' | 'nested_path')}>
          <SelectTrigger id="json-extraction-mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="single_field">Single Field Extraction</SelectItem>
            <SelectItem value="nested_path">Nested Path Extraction</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {extractionMode === 'single_field' ? (
        <div>
          <Label htmlFor="json-extraction-key">JSON Key</Label>
          <Input
            id="json-extraction-key"
            value={fieldName}
            onChange={(e) => {
              const val = e.target.value;
              setFieldName(val);
              if (val && !isValidJsonKey(val)) {
                setKeyError("Key contains invalid characters. Single quotes and backslashes are not allowed.");
              } else {
                setKeyError(null);
              }
            }}
            placeholder="Enter JSON field name (e.g., Client)"
            maxLength={MAX_PATH_LENGTH}
            aria-invalid={!!keyError}
            aria-describedby={
              keyError
                ? 'json-extraction-key-error'
                : !fieldName.trim()
                  ? 'json-extraction-key-hint'
                  : undefined
            }
          />
          {keyError && (
            <p id="json-extraction-key-error" role="alert" className="text-sm text-destructive mt-1">
              {keyError}
            </p>
          )}
          {!keyError && !fieldName.trim() && (
            <p id="json-extraction-key-hint" className="text-xs text-muted-foreground mt-1">
              Enter a field name to generate the extraction expression
            </p>
          )}
        </div>
      ) : (
        <div>
          <Label htmlFor="json-extraction-path">JSON Path</Label>
          <Input
            id="json-extraction-path"
            value={jsonPath}
            onChange={(e) => {
              const val = e.target.value;
              setJsonPath(val);
              if (val && !isValidJsonPath(val)) {
                setPathError('Path contains invalid characters. Only letters, numbers, dots, brackets, underscores, hyphens, * and # are allowed.');
              } else {
                setPathError(null);
              }
            }}
            placeholder="Enter path (e.g., address.city)"
            maxLength={MAX_PATH_LENGTH}
            aria-invalid={!!pathError}
            aria-describedby={
              pathError
                ? 'json-extraction-path-error'
                : !jsonPath.trim()
                  ? 'json-extraction-path-hint'
                  : undefined
            }
          />
          {pathError && (
            <p id="json-extraction-path-error" role="alert" className="text-sm text-destructive mt-1">
              {pathError}
            </p>
          )}
          {!pathError && !jsonPath.trim() && (
            <p id="json-extraction-path-hint" className="text-xs text-muted-foreground mt-1">
              Enter a field name to generate the extraction expression
            </p>
          )}
        </div>
      )}

      <div>
        <Label htmlFor="json-extraction-return-type">Return Type</Label>
        <Select value={returnType} onValueChange={setReturnType}>
          <SelectTrigger id="json-extraction-return-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RETURN_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {returnType !== 'VARCHAR' && (
        <div className="flex items-center gap-3">
          <Switch
            id="json-extraction-safe-cast"
            checked={safeCast}
            onCheckedChange={setSafeCast}
          />
          <Label htmlFor="json-extraction-safe-cast" className="cursor-pointer">
            Safe Cast (TRY_CAST - returns NULL on error)
          </Label>
        </div>
      )}

      <div className="text-sm text-muted-foreground">
        <strong>DuckDB JSON Extraction Reference:</strong>
        <ul className="list-disc list-inside mt-1 space-y-1">
          <li>Single field as text: <code>&quot;attributes&quot;-&gt;&gt;&apos;Client&apos;</code></li>
          <li>Nested path: <code>json_extract_string(&quot;attributes&quot;, &apos;$.address.city&apos;)</code></li>
          <li>With type cast: <code>TRY_CAST(&quot;attributes&quot;-&gt;&gt;&apos;Price&apos; AS DOUBLE)</code></li>
          <li>Returns NULL when the key does not exist</li>
        </ul>
      </div>
    </div>
  );
};
